import React from 'react';
import { DollarSign, Check, Users, Truck, Lightbulb, Volume2, Home } from 'lucide-react';
import { Button } from './ui/button';
import { flatRatePricing, capacityPricing } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const PricingSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-500/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/20 rounded-full px-4 py-2 mb-6">
            <DollarSign className="w-4 h-4 text-gold-400" />
            <span className="text-gold-300 text-sm font-medium">Transparent Pricing</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Investment in <span className="text-gold-400">Memorable Music</span>
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Clear, straightforward pricing with no hidden fees. All packages include lights, sound system, lodging, fuel, and travel expenses.
          </p>
        </div>

        {/* Pricing Tabs */}
        <Tabs defaultValue="flatrate" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-stone-900 p-1 rounded-xl mb-12">
            <TabsTrigger 
              value="flatrate" 
              className="data-[state=active]:bg-gold-500 data-[state=active]:text-white rounded-lg py-3 text-stone-400 font-medium transition-all"
            >
              Flat Rate
            </TabsTrigger>
            <TabsTrigger 
              value="capacity"
              className="data-[state=active]:bg-gold-500 data-[state=active]:text-white rounded-lg py-3 text-stone-400 font-medium transition-all"
            >
              Event Pricing
            </TabsTrigger>
          </TabsList>

          {/* Flat Rate Content */}
          <TabsContent value="flatrate">
            <div className="text-center mb-8">
              <p className="text-stone-400">{flatRatePricing.subtitle}</p>
              <p className="text-gold-400 text-sm mt-2">Effective {flatRatePricing.effectiveDate}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {flatRatePricing.packages.map((pkg, index) => {
                const pricingId = index === 0 ? 'pricing-solo' : index === 1 ? 'pricing-duo' : 'pricing-fullband';
                return (
                <div
                  key={index}
                  id={pricingId}
                  className={`relative bg-stone-900/50 border rounded-2xl p-8 ${
                    index === 1 ? 'border-gold-500/50 shadow-lg shadow-gold-500/10' : 'border-stone-800'
                  }`}
                >
                  {index === 1 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                      POPULAR
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold text-white mb-4">{pkg.name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-gold-400">{pkg.price}</span>
                    <span className="text-stone-400">{pkg.unit}</span>
                  </div>
                  
                  <p className="text-stone-400 mb-6">{pkg.details}</p>
                  
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-stone-300">
                      <div className="w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-gold-400" />
                      </div>
                      <span className="text-sm">Professional Sound System</span>
                    </div>
                    <div className="flex items-center gap-3 text-stone-300">
                      <div className="w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-gold-400" />
                      </div>
                      <span className="text-sm">Stage Lighting Included</span>
                    </div>
                    <div className="flex items-center gap-3 text-stone-300">
                      <div className="w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-gold-400" />
                      </div>
                      <span className="text-sm">Travel & Lodging Covered</span>
                    </div>
                  </div>

                  <Button
                    onClick={scrollToContact}
                    className={`w-full py-6 font-semibold rounded-xl transition-all duration-300 ${
                      index === 1
                        ? 'bg-gold-500 hover:bg-gold-600 text-white'
                        : 'bg-stone-800 hover:bg-stone-700 text-white'
                    }`}
                  >
                    Get Quote
                  </Button>
                </div>
              )})}
            </div>

            {/* Inclusions */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                { icon: Lightbulb, text: 'Stage Lights' },
                { icon: Volume2, text: 'Sound System' },
                { icon: Home, text: 'Lodging' },
                { icon: Truck, text: 'Travel & Fuel' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-xl p-4">
                  <item.icon className="w-5 h-5 text-gold-400" />
                  <span className="text-stone-300 text-sm">Included</span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Capacity Based Content */}
          <TabsContent value="capacity">
            <div className="text-center mb-8">
              <p className="text-stone-400">{capacityPricing.subtitle}</p>
              <p className="text-gold-400 text-sm mt-2 font-medium">{capacityPricing.minimum}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {capacityPricing.packages.map((pkg, index) => (
                <div
                  key={index}
                  className={`bg-stone-900/50 border rounded-2xl p-6 hover:border-gold-500/50 transition-all duration-300 ${
                    index === 2 ? 'border-gold-500/50 ring-1 ring-gold-500/20' : 'border-stone-800'
                  }`}
                >
                  <h3 className="text-lg font-bold text-white mb-2">{pkg.name}</h3>
                  {pkg.note && (
                    <p className="text-gold-400 text-xs mb-3">{pkg.note}</p>
                  )}
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-bold text-gold-400">{pkg.pricePerPerson}</span>
                    <span className="text-stone-400 text-sm">/person</span>
                  </div>
                  <p className="text-stone-500 text-xs mb-4">60 person minimum</p>
                  <div className="flex items-center gap-2 text-stone-400 text-sm bg-stone-950/50 rounded-lg p-3">
                    <Users className="w-4 h-4" />
                    <span>{pkg.example}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                onClick={scrollToContact}
                className="bg-gold-500 hover:bg-gold-600 text-white px-10 py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/20"
              >
                Calculate Your Event
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default PricingSection;
